import { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaImage, 
  FaEye, 
  FaEyeSlash,
  FaTags 
} from "react-icons/fa";
import axios from "axios";
import Header from "../../component/header/header";
import Sidebar from "../../component/sidebar/sidebar";
import "./categories.css";

// ✅ CORRECT API BASE URL
const API_BASE_URL = "https://api.brandelsuperadmin.starlighttechlabsindia.com/api";

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
    image: null
  });
  const [imagePreview, setImagePreview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failedImages, setFailedImages] = useState({});

  const token = localStorage.getItem("token");

  // ✅ FIXED: Image URL function - matches backend static file serving
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith("http")) return image;
    // Backend serves images from /images (configured in server.js)
    return `${API_BASE_URL}${image}`;
  };

  // ✅ FIXED: Fetch all categories - matches backend route: GET /admin/categories
  const fetchCategories = async () => {
    try {
      setLoading(true);

      console.log("Token:", token);

      const res = await axios.get(
        `${API_BASE_URL}/category/admin/categories`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("API Response:", res.data);
      console.log("Categories:", res.data.categories);

      setCategories(res.data.categories || []);
      setError("");
    } catch (err) {
      console.log("Status:", err.response?.status);
      console.log("Response:", err.response?.data);
      console.error(err);
      setError(err.response?.data?.message || "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB");
        return;
      }
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError("Only JPEG, PNG, GIF, and WEBP images are allowed");
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || "",
        status: category.status,
        image: null
      });
      if (category.image) {
        setImagePreview(getImageUrl(category.image));
      } else {
        setImagePreview("");
      }
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        description: "",
        status: "active",
        image: null
      });
      setImagePreview("");
    }
    setShowModal(true);
    setError("");
  };

  // ✅ FIXED: Handle submit - matches backend routes
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Validate name
    if (!formData.name.trim()) {
      setError("Category name is required");
      setSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("description", formData.description || "");
      formDataToSend.append("status", formData.status);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      if (editingCategory) {
        // ✅ Backend route: PUT /category/:id
        await axios.put(`${API_BASE_URL}/category/category/${editingCategory._id}`, formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        setSuccess("Category updated successfully!");
      } else {
        // ✅ Backend route: POST /category
        await axios.post(`${API_BASE_URL}/category/category`, formDataToSend, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        setSuccess("Category created successfully!");
      }

      await fetchCategories();
      setShowModal(false);
      setImagePreview("");
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ FIXED: Handle delete - matches backend route: DELETE /category/:id
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/category/category/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Category deleted successfully!");
      await fetchCategories();
    } catch (err) {
      console.error("Delete error:", err);
      setError(err.response?.data?.message || "Failed to delete category");
    }
  };

  // ✅ FIXED: Toggle status - matches backend route: PATCH /category/:id/status
  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.patch(`${API_BASE_URL}/category/category/${id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Category ${newStatus === "active" ? "activated" : "deactivated"}!`);
      await fetchCategories();
    } catch (err) {
      console.error("Status update error:", err);
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="loader">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <Header />
      
      {/* ✅ FIXED: Sidebar and main content inside same admin-layout */}
      <div className="admin-layout">
        <Sidebar />

        <main className="admin-content">
          <Container fluid>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="d-flex justify-content-between align-items-center mb-4"
            >
              <h4 className="page-title">
                <FaTags className="me-2" /> Category Management
              </h4>
              <Button variant="primary" onClick={() => openModal()}>
                <FaPlus className="me-2" /> Add Category
              </Button>
            </motion.div>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <motion.div
              className="table-wrapper"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Table hover responsive className="category-table">
                <thead>
                  <tr>
                    <th style={{ width: "15%" }}>Image</th>
                    <th style={{ width: "25%" }}>Name</th>
                    <th style={{ width: "30%" }}>Description</th>
                    <th style={{ width: "10%" }}>Status</th>
                    <th style={{ width: "20%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">No categories found</td>
                    </tr>
                  ) : (
                    categories.map((category, idx) => {
                      const imageUrl = getImageUrl(category.image);
                      const usePlaceholder = failedImages[category._id] || !imageUrl;
                      
                      return (
                        <motion.tr
                          key={category._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.05 }}
                        >
                          <td style={{ textAlign: "center" }}>
                            {usePlaceholder ? (
                              <div style={{
                                height: "50px",
                                width: "50px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#FF6B6B",
                                borderRadius: "8px",
                                color: "white",
                                fontSize: "20px",
                                fontWeight: "bold"
                              }}>
                                {category.name?.charAt(0).toUpperCase()}
                              </div>
                            ) : (
                              <img
                                src={imageUrl}
                                alt={category.name}
                                style={{ 
                                  height: "50px", 
                                  width: "50px",
                                  objectFit: "cover",
                                  borderRadius: "8px"
                                }}
                                onError={() => {
                                  console.log(`Image failed: ${imageUrl}`);
                                  setFailedImages(prev => ({ ...prev, [category._id]: true }));
                                }}
                              />
                            )}
                          </td>
                          <td className="fw-semibold">{category.name}</td>
                          <td className="text-muted">{category.description || "—"}</td>
                          <td>
                            <Badge bg={category.status === "active" ? "success" : "danger"}>
                              {category.status === "active" ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="action-buttons">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openModal(category)}
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant={category.status === "active" ? "outline-warning" : "outline-success"}
                              onClick={() => toggleStatus(category._id, category.status)}
                              title={category.status === "active" ? "Deactivate" : "Activate"}
                            >
                              {category.status === "active" ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(category._id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </motion.div>
          </Container>
        </main>
      </div>

      <Modal show={showModal} onHide={() => {
        setShowModal(false);
        setImagePreview("");
        setError("");
        setFormData({
          name: "",
          description: "",
          status: "active",
          image: null
        });
      }} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? "Edit Category" : "Add New Category"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter category name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter category description"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxHeight: "100px", 
                      maxWidth: "100px",
                      objectFit: "cover",
                      borderRadius: "8px"
                    }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false);
            setImagePreview("");
            setError("");
            setFormData({
              name: "",
              description: "",
              status: "active",
              image: null
            });
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : (editingCategory ? "Update" : "Create")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminCategories;